/**
 * coldInjuryModule.js - 2026-10-20 (W5, weather work; research: docs/research/RESEARCH_WEATHER_INJURIES.md)
 *
 * Cold injury (frostbite, trench foot, hypothermia, non-freezing cold injury) that the weather of the day causes among troops in the field, on top of the
 * non-battle casualties of the DNBI model. Before this the weather table carried hypothermia and frostbite risks that nothing read.
 *
 * cases = wounded of the day x COLD_INJURY_PER_WIA_REFERENCE x cold index of the weather x protection factor
 *   COLD_INJURY_PER_WIA_REFERENCE 0.28: WWII European theater, winter 1944-45: about 45,000 trench foot and frostbite cases against about 160,000 wounded
 *     (Army medical report of 31 March 1945; Fifth Army ratio of trench foot to battle casualties 1 to 3.5). DERIVED, summary only; it is an UPPER reference for
 *     poorly prepared troops in a wet snowy winter, so it equals the protection factor 1.0 (NONE).
 *   cold index of a weather = (hypothermia_risk - 1) / (hypothermia_risk of snow - 1), from operationsConfig WEATHER_CONDITIONS.medical: snow 1.0, extreme cold 2.0,
 *     mud season 0.33, rain 0.13, other weather 0 (no cold risk in the table). The snow reference is the WWII winter. EDUCATED GUESS (the table values are ours).
 *   protection factor (clothing, boots, dry socks, rotation, discipline): NONE 1.0, BASIC 0.5, STANDARD 0.25, EXCELLENT 0.1. EDUCATED GUESS (no study measures it).
 * The cases are anchored to the wounded because the WWII ratio is; peacetime surveillance rates (about 1 per 1,000 person-years) are far smaller and are not used.
 * Overlap: the DNBI model already holds a small cold injury share and a winter season modifier; this term adds on top of them (the share inside the DNBI is under a case a day).
 */
import { WEATHER_CONDITIONS } from '../../constants/operationsConfig.js';

export const COLD_INJURY_WEATHER_ON = true;   // false = the weather does not add cold injuries (as before)
export const COLD_INJURY_PER_WIA_REFERENCE = 0.28;
export const COLD_PROTECTION = Object.freeze({ NONE: 1.0, BASIC: 0.5, STANDARD: 0.25, EXCELLENT: 0.1 });
/** Share of cold injury cases that return to duty (the trench warfare category of dnbiRates: rtd_rate 0.60). */
export const COLD_INJURY_RTD_SHARE = 0.60;
export const COLD_INJURY_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Reference ratio 0.28 per wounded DERIVED from the WWII winter 1944-45 (summary only); weather index and protection factor are guesses.'
});

const SNOW_EXCESS = (WEATHER_CONDITIONS.SNOW.medical.hypothermia_risk - 1);

/** Cold index of an operation weather: 0 when the table has no cold risk, 1 at snow. */
export function coldIndexOfWeather(weather) {
  const w = WEATHER_CONDITIONS[weather];
  if (!w) throw new Error(`coldInjuryModule: unknown weather '${weather}' (valid: ${Object.keys(WEATHER_CONDITIONS).join(', ')})`);
  const risk = w.medical?.hypothermia_risk;
  return typeof risk === 'number' ? Math.max(0, (risk - 1) / SNOW_EXCESS) : 0;
}

/**
 * @param {Object} c weather (operation weather key of the day), wounded (battle wounded of the day), protection (COLD_PROTECTION key)
 * @returns {{cases:number, cold_index:number, protection_factor:number, per_wounded:number}}
 */
export function coldInjuryOfDay({ weather, wounded, protection }) {
  if (!Object.prototype.hasOwnProperty.call(COLD_PROTECTION, protection)) {
    throw new Error(`coldInjuryModule: unknown cold protection '${protection}' (valid: ${Object.keys(COLD_PROTECTION).join(', ')})`);
  }
  if (typeof wounded !== 'number' || Number.isNaN(wounded) || wounded < 0) throw new Error(`coldInjuryModule: wounded must be a number >= 0 (got: ${wounded})`);
  const index = coldIndexOfWeather(weather);
  const factor = COLD_PROTECTION[protection];
  const perWounded = COLD_INJURY_PER_WIA_REFERENCE * index * factor;
  return { cases: COLD_INJURY_WEATHER_ON ? wounded * perWounded : 0, cold_index: index, protection_factor: factor, per_wounded: COLD_INJURY_WEATHER_ON ? perWounded : 0 };
}
